import React, { useState, useEffect, useCallback } from "react";
import { X, MessageSquare, Bell, CheckCircle, AlertCircle, AlertTriangle, Info, Phone } from "lucide-react";
import apiService from "../services/api";

const NotificationToast = ({ notification, onClose, onAcknowledge }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  const handleClose = useCallback(() => {
    setIsLeaving(true);
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose]);

  // Verificar se é uma notificação de intervenção humana (WhatsApp)
  const isHumanIntervention = !notification.type && notification.chat_id && notification.chat_id.includes('@s.whatsapp.net');

  useEffect(() => {
    // Animação de entrada
    const timer = setTimeout(() => setIsVisible(true), 100);

    // Auto-close - 8 segundos para intervenção humana, 4 segundos para outras
    const autoCloseTime = isHumanIntervention ? 8000 : 4000;
    const autoCloseTimer = setTimeout(() => {
      handleClose();
    }, autoCloseTime);

    return () => {
      clearTimeout(timer);
      clearTimeout(autoCloseTimer);
    };
  }, [handleClose, isHumanIntervention]);

  const handleClick = async () => {
    // Se for uma notificação genérica (sucesso/erro), apenas fecha ao clicar
    if (notification.type && ['success', 'error', 'warning', 'info'].includes(notification.type)) {
      handleClose();
      return;
    }

    try {
      // Marcar como acknowledged
      await apiService.markNotificationAsRead(notification.id);
      onAcknowledge(notification.id);

      // Detectar se é Instagram ou WhatsApp
      if (notification.chat_id) {
        const isInstagram = !notification.chat_id.includes('@s.whatsapp.net');

        if (isInstagram) {
          // Para Instagram, apenas marcar como lida - não abrir link
          console.log('Notificação do Instagram marcada como lida');
        } else {
          // Para WhatsApp, navegar para /chats com o telefone como parâmetro
          const phoneNumber = notification.chat_id.replace(/\D/g, "").replace('swhatsappnet', '');
          window.location.href = `/chats?phone=${phoneNumber}&notificationId=${notification.id}`;
        }
      }

      handleClose();
    } catch (error) {
      console.error('Erro ao processar notificação:', error);
    }
  };

  // Renderizar toast especial para intervenção humana (WhatsApp)
  if (isHumanIntervention) {
    const phoneNumber = notification.chat_id?.split("@")[0];
    const formattedPhone = phoneNumber?.replace(/(\d{2})(\d{2})(\d{5})(\d{4})/, '+$1 ($2) $3-$4');

    return (
      <div
        className={`
          w-96 transform transition-all duration-500 ease-out
          ${isVisible && !isLeaving
            ? "translate-x-0 opacity-100 scale-100"
            : "translate-x-full opacity-0 scale-90"
          }
        `}
      >
        <div
          className="relative overflow-hidden rounded-2xl shadow-2xl cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:shadow-3xl"
          onClick={handleClick}
        >
          {/* Background gradient vibrante */}
          <div className="absolute inset-0 bg-gradient-to-br from-green-500 via-emerald-500 to-teal-600"></div>

          {/* Padrão decorativo */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
          </div>

          {/* Animação de pulse no fundo */}
          <div className="absolute inset-0 animate-pulse bg-white/5"></div>

          {/* Conteúdo */}
          <div className="relative p-5">
            {/* Header com ícone e título */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                {/* Ícone WhatsApp com animação */}
                <div className="relative">
                  <div className="absolute inset-0 bg-white rounded-full animate-ping opacity-30"></div>
                  <div className="relative w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg">
                    <svg className="w-7 h-7 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg leading-tight">
                    🚨 Ajuda Humana
                  </h3>
                  <p className="text-green-100 text-sm">
                    Cliente precisa de atendimento
                  </p>
                </div>
              </div>

              {/* Botão de fechar */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                }}
                className="p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              >
                <X size={16} className="text-white" />
              </button>
            </div>

            {/* Info do contato */}
            <div className="bg-white/15 backdrop-blur-sm rounded-xl p-3 mb-4">
              <div className="flex items-center gap-2 mb-1">
                <Phone size={14} className="text-green-100" />
                <span className="text-white font-medium text-sm">
                  {formattedPhone || phoneNumber}
                </span>
              </div>
              {notification.last_message_snippet && (
                <p className="text-green-100 text-sm line-clamp-2 mt-1">
                  "{notification.last_message_snippet}"
                </p>
              )}
            </div>

            {/* CTA Button */}
            <button
              className="w-full py-3 px-4 bg-white text-green-600 font-bold rounded-xl flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
            >
              <MessageSquare size={18} />
              Abrir Conversa Agora
            </button>

            {/* Barra de progresso */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
              <div
                className="h-full bg-white/60"
                style={{
                  animation: 'shrink 8s linear forwards',
                  width: '100%'
                }}
              ></div>
            </div>
          </div>
        </div>

        {/* CSS para animação da barra */}
        <style>{`
          @keyframes shrink {
            from { width: 100%; }
            to { width: 0%; }
          }
        `}</style>
      </div>
    );
  }

  // Toast padrão para outras notificações
  return (
    <div
      className={`
        w-80 transform transition-all duration-300 ease-in-out
        ${isVisible && !isLeaving
          ? "translate-x-0 opacity-100 scale-100"
          : "translate-x-full opacity-0 scale-95"
        }
      `}
    >
      <div
        className="
          rounded-lg shadow-lg border
          p-4 cursor-pointer notification-toast transition-all duration-200
          relative overflow-hidden hover:shadow-xl
        "
        style={{
          background: 'var(--color-surface-container)',
          borderColor: 'rgba(255,255,255,0.08)',
        }}
        onClick={handleClick}
      >
        {/* Barra de progresso */}
        <div className="absolute top-0 left-0 h-1 notification-gradient animate-progress w-full"></div>

        {/* Botão de fechar */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
          style={{ color: 'var(--color-on-surface-variant)' }}
          className="absolute top-2 right-2 transition-colors hover:opacity-100 opacity-60"
        >
          <X size={16} />
        </button>

        {/* Conteúdo da notificação */}
        <div className="flex items-start space-x-3 pr-6">
          {notification.type === 'success' && (
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-green-500/15 rounded-full flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400 notification-icon" />
              </div>
            </div>
          )}

          {notification.type === 'error' && (
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-red-500/15 rounded-full flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-400 notification-icon" />
              </div>
            </div>
          )}

          {notification.type === 'warning' && (
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-yellow-500/15 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-yellow-400 notification-icon" />
              </div>
            </div>
          )}

          {notification.type === 'info' && (
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-blue-500/15 rounded-full flex items-center justify-center">
                <Info className="w-5 h-5 text-blue-400 notification-icon" />
              </div>
            </div>
          )}

          {/* Fallback para Instagram */}
          {!notification.type && (
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-purple-500/15 rounded-full flex items-center justify-center">
                <Bell className="w-5 h-5 text-purple-400 notification-icon" />
              </div>
            </div>
          )}

          <div className="flex-1 min-w-0">
            {notification.type ? (
              <>
                <div className="flex items-center space-x-2 mb-1">
                  <h4 className="text-sm font-semibold" style={{ color: 'var(--color-on-surface)' }}>
                    {notification.title}
                  </h4>
                </div>
                <p className="text-sm" style={{ color: 'var(--color-on-surface-variant)' }}>
                  {notification.message}
                </p>
              </>
            ) : (
              <>
                <div className="flex items-center space-x-2 mb-1">
                  <h4 className="text-sm font-semibold" style={{ color: 'var(--color-on-surface)' }}>
                    Intervenção Necessária - Instagram
                  </h4>
                </div>
                <p className="text-sm" style={{ color: 'var(--color-on-surface-variant)' }}>
                  Verificar solicitação no Instagram
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationToast;